import React, { createContext, useState, useEffect, useRef, ReactNode, useContext } from 'react';
import { login as apiLogin, logout as apiLogout, getUserDetails, type ApiResponse } from '../api/apiService';
import { track, resetIdentity, identify, setUserRole } from '../lib/analytics';
import { getItem, removeItem, setItem } from '../lib/storage';
import { clearOwnerDriverStorage, clearOwnerDriverStorageIfAccountChanged } from '../features/owner/ownerDriverPrompt';
import sentry from '../utils/sentry';

export interface LoginResult {
  success: boolean;
  response?: ApiResponse<any>;
}

interface AuthContextType {
  userToken: string | null;
  login: (email: string, password: string) => Promise<LoginResult>;
  logout: () => Promise<void>;
  isLoading: boolean;
  authenticateWithToken: (token: string, accountEmail?: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  userToken: null,
  login: async () => ({ success: false }),
  logout: async () => {},
  isLoading: false,
  authenticateWithToken: async () => {},
});

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [userToken, setUserToken] = useState<string | null>(null);
  const identityEpoch = useRef(0);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const loadToken = async () => {
    try {
      const token = await getItem('userToken');
      if (token) {
        setUserToken(token);
      }
    } catch (e) {
      console.error('Failed to load token', e);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadToken();
  }, []);

  const login = async (email: string, password: string): Promise<LoginResult> => {
      try {
        const response = await apiLogin(email, password);
        if (response.ok && response.data.key) {
          await clearOwnerDriverStorageIfAccountChanged(email);
          await setItem('userToken', response.data.key);
          setUserToken(response.data.key);
          // Attach the user to the Sentry scope so any subsequent error/crash
          // event carries who hit it. Cleared on logout below.
          sentry.setUserContext({ email });
          track('login_success', { user_role: 'unknown' });
          track('Sign In', { login_method: 'email', success: true });
          // Establish analytics identity AFTER login returns (fire-and-forget —
          // must never delay the login result). useUser re-establishes identity
          // on its own users/me fetch.
          void establishIdentity();
          return { success: true };
        }
        track('login_failed', { status: response.status });
        track('Sign In', { login_method: 'email', success: false });
        return { success: false, response };
      } catch (e) {
        track('login_failed', { error: e instanceof Error ? e.message : 'unknown' });
        track('Sign In', { login_method: 'email', success: false });
        console.error('Login error', e);
        return { success: false };
      }
    };

    // Establish the Mixpanel/analytics distinct_id. Runs as fire-and-forget so a
    // slow or failing GET /users/me + identify/setUserRole can NEVER delay or
    // break login/authenticateWithToken (MR !389 blocker 4). useUser independently
    // re-establishes identity on its own users/me fetch, so this is best-effort.
    // Each auth action (login, token auth, logout) starts a new identity epoch; a
    // slower users/me from a superseded epoch must not overwrite the newer actor.
    const establishIdentity = async (): Promise<void> => {
      const epoch = ++identityEpoch.current;
      try {
        const me = await getUserDetails();
        if (epoch !== identityEpoch.current) return;
        if (!me.ok || !me.data?.id) return;
        identify(String(me.data.id));
        if (Array.isArray(me.data.groups)) {
          const groups = me.data.groups ?? [];
          if (groups.includes('Owner')) setUserRole('owner');
          else if (groups.includes('Driver')) setUserRole('driver');
          else setUserRole('unknown');
        }
      } catch {
        // identity is also established by useUser; swallow here
      }
    };

  const logout = async () => {
    try {
      await apiLogout();
    } catch (e) {
      console.error('Logout error', e);
    } finally {
      identityEpoch.current += 1;
      await Promise.all([
        removeItem('userToken'),
        clearOwnerDriverStorage(),
      ]);
      setUserToken(null);
      // Clear identity from the Sentry scope so post-logout events aren't
      // attributed to the previous user/company.
      sentry.clearContext();
      resetIdentity();
      track('logout');
    }
  };

  const authenticateWithToken = async (token: string, accountEmail?: string): Promise<void> => {
    if (!token.trim()) {
      throw new Error('authenticateWithToken: token must not be blank');
    }
    if (accountEmail) {
      await clearOwnerDriverStorageIfAccountChanged(accountEmail);
    }
    await setItem('userToken', token);
    setUserToken(token);
    // Establish the Mixpanel/analytics distinct_id AFTER authentication returns.
    // Fired as fire-and-forget (MR !389 blocker 4): a slow or failing
    // GET /users/me + identify/setUserRole MUST NOT delay or block the
    // authenticateWithToken resolution — useUser re-establishes identity on its
    // own users/me fetch, so this is purely best-effort.
    void establishIdentity();
  };

  return (
    <AuthContext.Provider value={{ userToken, login, logout, isLoading, authenticateWithToken }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext); 